import { Directive, ContentChildren, ContentChild, ElementRef, Input, forwardRef } from '@angular/core';
import { VldMessageComponent } from 'components/vld-message/vld-message';
import { QueryList } from '@angular/core';
import { VldButtonDirective } from 'directives/vld-button/vld-button';
import { VldProgressDirective } from 'directives/vld-progress/vld-progress';
import { VldButtonSkipDirective } from 'directives/vld-button-skip/vld-button-skip';
import { VldGroupDirectiveInterface, VldGroupState } from './vld-group-interface';

@Directive({
  selector: '[vld-group]',
  standalone: false,
  exportAs: 'vldGroup'
})
export class VldGroupDirective implements VldGroupDirectiveInterface {
  @ContentChildren(forwardRef(() => VldMessageComponent), { descendants: true })
  messages: QueryList<VldMessageComponent>;
  @ContentChild(VldButtonDirective) button: VldButtonDirective;
  @ContentChild(VldButtonSkipDirective) buttonSkip: VldButtonSkipDirective;
  @Input() debug: string;

  stepNumber: number;
  displayStyleOrg: string; // style.display originally setup with
  progress: VldProgressDirective;
  state: VldGroupState = VldGroupState.Uninitialized;

  constructor(public element: ElementRef) { }

  getState() {
    return this.state;
  }

  ngOnInit() {
    this.displayStyleOrg = this.element.nativeElement.style.display;
    this.element.nativeElement.onNextClicked = this.onNextClicked.bind(this);
  }

  ngAfterContentInit() {
    if (this.button) {
      this.button.group = this;
    }
    if (this.buttonSkip) {
      this.buttonSkip.group = this;
    }

    this.messages.forEach(m => (m.group = this));
    this.messages.changes.subscribe(m => (m.group = this));
  }

  onFirstInit() {
    this.state = VldGroupState.Uninitialized;
    this.determineGroupState();
  }

  onMessagesChanged() {
    this.determineGroupState();
    if (!this.progress) return;
    this.progress.onProgressChanged();
  }

  determineGroupState() {
    if (!this.messages || !this.progress) {
      return;
    }

    let error = this.messages.find(m => m.validity != null && !m.warning);

    switch (this.state) {
      default:
      case VldGroupState.Uninitialized:
        this.state = error
          ? VldGroupState.Naive //<<< user hasn't completed the fields yet
          : VldGroupState.Ready;
        break;
      case VldGroupState.Naive:
      case VldGroupState.Ready:
        this.state = error
          ? VldGroupState.Naive //<<< user hasn't completed the fields yet
          : VldGroupState.Ready;
        break;
      case VldGroupState.Errors:
        this.state = error ? VldGroupState.Errors : VldGroupState.Ready;
        break;
      case VldGroupState.Done:
        this.state = error ? VldGroupState.Errors : VldGroupState.Done;
        break;
    }

    // if re-loading/re-correcting a group where the next button was already clicked before
    if (this.state == VldGroupState.Ready && this.progress.maxStep > this.stepNumber) {
      this.state = VldGroupState.Done;
    }
  }

  adjustUI() {
    // if disableHiding is true, always show the element
    if (this.progress.disableHiding) {
      this.element.nativeElement.style.display = this.displayStyleOrg;
    } else {
      if (this.stepNumber > this.progress.currentStep) {
        this.element.nativeElement.style.display = 'none';
      } else {
        this.element.nativeElement.style.display = this.displayStyleOrg;
      }
    }

    if (this.button) {
      this.button.showButton(this.stepNumber == this.progress.currentStep);
      //this.button.enableButton(this.state == VldGroupState.Naive || this.state == VldGroupState.Ready || this.state == VldGroupState.Done);
    }
    if (this.buttonSkip) {
      this.buttonSkip.showButton(this.stepNumber == this.progress.currentStep);
    }
  }

  onProgressChanged() {
    this.adjustUI();
  }

  // user clicked on the Next button
  public onNextClicked(event?: any) {
    void event;
    this.determineGroupState();

    if (this.state === VldGroupState.Ready) {
      this.state = VldGroupState.Done;
    } else if (this.state !== VldGroupState.Done) {
      this.state = VldGroupState.Errors;
    }

    // changing state might require messages to be updated (if changed from Naive to Error)!
    this.messages.forEach(m => {
      m.group = this;
      m.updateText();
    });
    this.progress.onProgressChanged();

    // if there are validation errors, scroll to the first error message
    if (this.state === VldGroupState.Errors) {
      const firstErrorMessage = this.messages.find(m => m.validity != null && !m.warning);
      if (firstErrorMessage) {
        // vld-message has display: contents, so find the actual .error div child
        const messageElement = firstErrorMessage.elementRef.nativeElement as HTMLElement;
        const errorDiv = messageElement.querySelector('.error') as HTMLElement;
        const scrollTarget = errorDiv || messageElement.parentElement || messageElement;
        // force scroll even if autoScroll is disabled
        void this.progress.scrollToElementAsync(scrollTarget);
      }
    }
  }

  // user clicked on the Next button
  onSkipClicked(event: any) {
    void event;
    this.state = VldGroupState.Done;
    this.progress.onProgressChanged();
  }
}
